import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { getAuth, onAuthStateChanged, User } from 'firebase/auth';
import { getFirebaseApp } from '../core/firebase';
import { OrgContextService } from '../core/services/org-context.service';

function retrieveUser(): Promise<User | null> {
  const auth = getAuth(getFirebaseApp());
  return new Promise((resolve) => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      unsubscribe();
      resolve(user && !user.isAnonymous ? user : null);
    });
  });
}

/**
 * Crewdle super-admin: an @crewdle.com user whose current org is "Crewdle".
 * This is the same gate the pages used in admin-console (the old `crewdleOnly`
 * guard) and now governs the global-config pages here.
 */
function isCrewdleSuperAdmin(user: User | null, orgContext: OrgContextService): boolean {
  return !!user?.email?.endsWith('@crewdle.com') && orgContext.currentOrg()?.name === 'Crewdle';
}

/**
 * Restricts to Crewdle super-admins. Gates global platform-config pages
 * (models, products-config, workflows-overview) that resellers must not edit.
 */
export const crewdleSuperAdmin: CanActivateFn = async () => {
  const router = inject(Router);
  const orgContext = inject(OrgContextService);
  const user = await retrieveUser();
  if (isCrewdleSuperAdmin(user, orgContext)) return true;
  // Partners still have somewhere to land; customers fall through there too.
  return router.parseUrl(orgContext.orgUrl('/all-organizations'));
};

/**
 * Allows Crewdle super-admins OR resellers — members whose current org is
 * flagged `isPartner`. Gates the client/partner management page
 * (all-organizations), which the backend scopes to the caller's clients.
 */
export const partnerOrCrewdle: CanActivateFn = async () => {
  const router = inject(Router);
  const orgContext = inject(OrgContextService);
  const user = await retrieveUser();
  if (isCrewdleSuperAdmin(user, orgContext)) return true;
  if (orgContext.currentOrg()?.isPartner === true) return true;
  // No partner access — land on the in-app partner application form.
  return router.parseUrl(orgContext.orgUrl('/become-partner'));
};
